import React, { createContext, useState, useEffect, useContext, useRef, useMemo } from 'react';
import { authService } from '../services/authService';
import animalHierarchyCache from '../services/animalHierarchyCache';
import { disconnectRealtime } from '../services/realtime';
import { clearCachedApiData } from '../utils/clearCachedApiData';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls from StrictMode
    if (initRef.current) return;
    initRef.current = true;

    const initAuth = () => {
      // Use stored user data immediately (no API call on refresh)
      const storedUser = authService.getStoredUser();
      if (storedUser && authService.isAuthenticated()) {
        setUser(storedUser);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // The stored user's permissions can be out of date (an owner changed this member's modules
  // after they signed in). Re-read them from the server on open and when the tab is shown again.
  useEffect(() => {
    let lastSync = 0;
    const syncUser = async () => {
      if (!authService.isAuthenticated() || Date.now() - lastSync < 60000) return;
      lastSync = Date.now();
      try {
        const data = await authService.getCurrentUser();
        const fresh = data?.user;
        if (fresh && authService.isAuthenticated()) {
          setUser(fresh);
          localStorage.setItem('user', JSON.stringify(fresh));
        }
      } catch (error) {
        // Offline or server error: keep the stored user (401 is handled by the api interceptor).
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncUser();
    };

    syncUser();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const login = async (credentials) => {
    const data = await authService.login(credentials);
    // Drop any cached responses left from a previous user on this device
    await clearCachedApiData();
    setUser(data.user);
    return data;
  };

  const workerSignIn = async (linkToken, credentials) => {
    const data = await authService.workerSignIn(linkToken, credentials);
    await clearCachedApiData();
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const data = await authService.register(userData);
    setUser(data.user);
    return data;
  };

  const logout = async (originalAdminToken = null) => {
    try {
      const adminToken =
        originalAdminToken ?? localStorage.getItem('original_admin_token');
      await authService.logout(adminToken);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API call fails, clear local state
    } finally {
      // Clear per-user cached hierarchy (prevents breeds leaking across logins)
      animalHierarchyCache.clear();
      // Close the live notification connection so the next user gets their own.
      disconnectRealtime();
      // Remove this user's cached API responses (offline cache) from the device.
      await clearCachedApiData();
      setUser(null);
      // Clear impersonation data
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('original_admin_user');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Switch to another account (super admin "Login as User" and "Return to admin").
  // Does a full page load instead of re-rendering the current screen as the other user:
  // that mixed state (admin page + farm user + old cached data) could crash to a blank page.
  const switchUser = async (token, userData, path) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    animalHierarchyCache.clear();
    disconnectRealtime();
    await clearCachedApiData();
    window.location.replace(path);
  };

  // Optional: Refresh user data from server when needed
  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
      localStorage.setItem('user', JSON.stringify(userData.user));
      return userData.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // If refresh fails, user might be logged out on server
      // Keep using stored data unless explicitly needed
      throw error;
    }
  };

  // Make isAuthenticated reactive - it should update when user state changes
  const isAuthenticated = useMemo(() => {
    return !!user && authService.isAuthenticated();
  }, [user]);

  const value = {
    user,
    loading,
    login,
    workerSignIn,
    register,
    logout,
    updateUser,
    switchUser,
    refreshUser,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

